import { AppNavParams } from '@euler/app/Routes';
import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { useNavigateToTaskListItem } from '@euler/app/flows/common/functions/useNavigateToTaskListItem';
import { CreateOrderFab } from '@euler/app/flows/home/components/CreateOrderFab';
import { TabContext } from '@euler/app/flows/home/functions/TabContext';
import { useDashboardHeader } from '@euler/app/flows/home/functions/useDashboardHeader';
import { useDashboardTasks } from '@euler/app/flows/home/functions/useDashboardTasks';
import { useTaskListLayout } from '@euler/app/flows/home/functions/useTaskListLayout';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import { useRefreshFeedback } from '@euler/functions/useRefreshFeedback';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { TaskListItem } from '@euler/model/TaskListItem';
import { InspectionTaskStatus } from '@euler/model/enum';
import { CameraTestButton } from '@euler/playground/CameraTestButton';
import { delayed, onErrorIgnore, safeMarkDevFlag } from '@euler/utils';
import { usePersistFn, usePrevious } from '@euler/utils/hooks';
import {
  BottomTabNavigationOptions,
  useBottomTabBarHeight,
} from '@react-navigation/bottom-tabs';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import { DashboardHeader } from '../components/DashboardHeader';
import { TaskList, TaskListRef } from '../components/TaskList';

const kShowCameraTestButton = safeMarkDevFlag(false) && Platform.OS !== 'web';

// 1366x1024
export const DashboardScreen = wrapNavigatorScreen<
  Record<string, any>,
  BottomTabNavigationOptions
>(
  () => {
    useAIAssistantForPage('task_list');
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    const playRefreshFeedback = useRefreshFeedback();
    const navigateToTaskItem = useNavigateToTaskListItem();
    const tabHeight = useBottomTabBarHeight();
    const taskListRef = useRef<TaskListRef>(null);
    const [userSettings] = useUserLocalSettings();
    const useV2Card = userSettings?.preferLegacyTaskCard !== true;
    const { width, numberOfColumns } = useTaskListLayout();
    const limit = numberOfColumns <= 1 ? 15 : numberOfColumns * 10;
    const { event$ } = useOrderContext();

    const {
      segments,
      tags,
      segmentIndex,
      activeTag,
      assignedToMe,
      setSegmentIndex,
      setActiveTag,
    } = useDashboardHeader();

    const {
      data,
      status,
      refresh,
      update,
      fetchSinceLastError,
      onScrollToBottom,
    } = useDashboardTasks({
      assignedToMe,
      tag: activeTag,
      limit,
      onRefresh: () => {
        taskListRef.current?.scrollToTop();
      },
    });

    const { isLoading, isRefreshing, error, hasMore } = status;

    const onRefresh = useCallback(() => {
      refresh().then(playRefreshFeedback).catch(onErrorIgnore);
    }, [refresh, playRefreshFeedback]);

    const onTaskPress = useCallback(
      (item: TaskListItem) => {
        navigateToTaskItem(item);
      },
      [navigateToTaskItem],
    );

    const onSearchPress = useCallback(() => {
      navigation.navigate('_search', {
        onPressResultItem: item => {
          console.log(item);
        },
      });
    }, [navigation]);

    const { scrollY } = useContext(TabContext);

    const prevSegmentIndex = usePrevious(segmentIndex);
    useEffect(() => {
      if (prevSegmentIndex != null && segmentIndex !== prevSegmentIndex) {
        onRefresh();
      }
    }, [segmentIndex, prevSegmentIndex, onRefresh]);

    const prevTag = usePrevious(activeTag);
    useEffect(() => {
      if (prevTag != activeTag) {
        onRefresh();
      }
    }, [activeTag, onRefresh, prevTag]);

    const stopScrollEvent = useRef(false);

    const handleScrollToBottom = usePersistFn(() => {
      if (hasMore) {
        stopScrollEvent.current = true;
      }
      delayed(() => {
        onScrollToBottom();
      }, 100);
    });

    useEffect(() => {
      stopScrollEvent.current = false;
    }, [data.length]);

    const onScrollOffsetChange = usePersistFn((offset: number) => {
      if (stopScrollEvent.current) return;
      scrollY.value = offset;
    });

    const navigateToTask = usePersistFn((taskNo: string) => {
      navigation.push('Task', { taskNo });
    });

    const onTaskCanceled = usePersistFn(
      (orderNo: string, reason: string | undefined) => {
        const index = data.findIndex(x => x.orderNo === orderNo);
        if (index >= 0) {
          update(item => {
            item.status = InspectionTaskStatus.Cancelled;
            item.canceledAt = new Date();
            item.cancelReason = reason;
            return undefined;
          });
        }
      },
    );

    const updateSubscribeStatus = usePersistFn(
      (orderNo: string, subscribed: boolean) => {
        const index = data.findIndex(x => x.orderNo === orderNo);
        if (index >= 0) {
          update(item => {
            if (item.orderNo === orderNo) {
              item.subscribed = subscribed;
            }
            return undefined;
          });
        }
      },
    );

    useEffect(() => {
      const sub = event$.subscribe(event => {
        if (!event.isMainOrder) return;
        if (event.type === 'rollback') {
          void refresh().finally(() => {
            delayed(() => {
              navigateToTask(event.taskNo);
            }, 300);
          });
        } else if (event.type === 'canceled') {
          onTaskCanceled(event.orderNo, event.extra);
        } else if (
          event.type == 'subscribed' ||
          event.type === 'unsubscribed'
        ) {
          updateSubscribeStatus(event.orderNo, event.type === 'subscribed');
        }
      });
      return () => sub.unsubscribe();
    }, [
      refresh,
      navigateToTask,
      event$,
      updateSubscribeStatus,
      onTaskCanceled,
    ]);

    useEffect(() => {
      delayed(onRefresh, 1000);
    }, [segments.length, onRefresh]);

    return (
      <>
        <View
          css={`
            flex: 1;
            background-color: rgb(235, 233, 237);
          `}
        >
          <DashboardHeader
            segments={segments}
            tags={tags}
            activeTag={activeTag}
            defaultTitle="门店看板"
            onTagChange={setActiveTag}
            segmentIndex={segmentIndex}
            showSidebarTrigger={true}
            onSegmentIndexChange={setSegmentIndex}
            onSearchPress={onSearchPress}
            onRefresh={onRefresh}
          />

          <TaskList
            width={width}
            ref={taskListRef}
            columns={numberOfColumns}
            data={data}
            extraBottomSpacing={tabHeight}
            useV2Card={useV2Card}
            showRollbackDetail={numberOfColumns === 1}
            showCancelReason={numberOfColumns === 1}
            isRefreshing={isRefreshing}
            isLoading={isLoading}
            error={error}
            hasMore={hasMore}
            onRetry={fetchSinceLastError}
            onTaskPress={onTaskPress}
            onRefresh={onRefresh}
            onScrollToBottom={handleScrollToBottom}
            onScrollOffsetChange={onScrollOffsetChange}
            css={`
              flex: 1;
              align-self: stretch;
            `}
          />
          <CreateOrderFab />
        </View>
        {kShowCameraTestButton && <CameraTestButton />}
      </>
    );
  },
  {
    title: '门店看板',
    headerShown: false,
  },
);
